import { useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { ArrowLeft, Plus, X, Trash2, Edit2, Check } from 'lucide-react'
import { format } from 'date-fns'
import ProductPicker from '../components/ProductPicker'
import { loadOrderStates, shipmentSelectValue, SHIPMENT_STATUS_OPTIONS } from '../lib/orderStatus'
import HelpLink from '../components/HelpLink'


function QtyCell({ line, field, onSave }) {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(line[field] ?? '')

  function commit() {
    onSave(line.id, field, val)
    setEditing(false)
  }

  if (editing) {
    return (
      <input
        autoFocus
        className="input"
        type="number"
        value={val}
        style={{ width: '72px', padding: '2px 6px', textAlign: 'right', fontSize: '13px' }}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === 'Enter') commit()
          if (e.key === 'Escape') { setVal(line[field] ?? ''); setEditing(false) }
        }}
      />
    )
  }
  return (
    <span
      onClick={() => { setVal(line[field] ?? ''); setEditing(true) }}
      title="Click to edit"
      style={{ cursor: 'text', padding: '2px 6px', borderRadius: '4px', display: 'inline-block', minWidth: '32px', textAlign: 'right', fontWeight: field === 'quantity_received' ? 500 : 400, color: line[field] != null ? 'var(--text-1)' : 'var(--text-3)' }}
    >
      {line[field] ?? '—'}
    </span>
  )
}

export default function ShipmentDetail() {
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { activeBrand, user } = useAuth()
  const navigate = useNavigate()
  const [shipment, setShipment] = useState(null)
  const [landed, setLanded] = useState(false)
  const [lines, setLines] = useState([])
  const [allProducts, setAllProducts] = useState([])
  const [poProducts, setPoProducts] = useState([])
  const [locations, setLocations] = useState([])
  const [poLines, setPoLines] = useState([])
  const [lineModal, setLineModal] = useState(false)
  const [showAllProducts, setShowAllProducts] = useState(false)
  const [lineForm, setLineForm] = useState({ product_id: '', quantity_ordered: '', lot_number: '', expiration_date: '', notes: '' })
  const [saving, setSaving] = useState(false)
  const [editingHeader, setEditingHeader] = useState(false)
  const [headerForm, setHeaderForm] = useState({})
  const [pos, setPos] = useState([])

  useEffect(() => { load() }, [id])

  useEffect(() => {
    if (searchParams.get('addItem') === 'true' && shipment) setLineModal(true)
  }, [searchParams, shipment])

  async function load() {
    const [{ data: s }, { data: ls }, { data: ps }, { data: locs }, { data: poData }] = await Promise.all([
      supabase.from('shipments').select('*, purchase_orders(po_number, id), ship_to_loc:ship_to_location_id(name, type)').eq('id', id).single(),
      supabase.from('shipment_line_items').select('*, products(name, sku)').eq('shipment_id', id),
      activeBrand ? supabase.from('products').select('id, name, sku').eq('brand_id', activeBrand.id).is('deleted_at', null).order('sku') : Promise.resolve({ data: [] }),
      activeBrand ? supabase.from('locations').select('id, name, type').eq('brand_id', activeBrand.id).eq('is_active', true).order('name') : Promise.resolve({ data: [] }),
      activeBrand ? supabase.from('purchase_orders').select('id, po_number').eq('brand_id', activeBrand.id).is('deleted_at', null).order('po_number') : Promise.resolve({ data: [] })
    ])
    setShipment(s)
    if (activeBrand) { try { setLanded((await loadOrderStates(activeBrand.id)).shipmentState[id] === 'landed') } catch (e) { setLanded(false) } }
    setLines(ls || [])
    setAllProducts(ps || [])
    setLocations(locs || [])
    setPos(poData || [])

    if (s?.po_id) {
      const { data: pols } = await supabase.from('po_line_items').select('*, products(name, sku, id)').eq('po_id', s.po_id)
      setPoLines(pols || [])
      setPoProducts((pols || []).map(l => l.products).filter(Boolean))
    } else {
      setPoLines([])
      setPoProducts([])
    }
  }

  const dropdownProducts = showAllProducts || poProducts.length === 0 ? allProducts : poProducts

  function startEditHeader() {
    setHeaderForm({
      po_id: shipment.po_id || '',
      supplier_name: shipment.supplier_name || '',
      carrier: shipment.carrier || '',
      tracking_number: shipment.tracking_number || '',
      expected_ship_date: shipment.expected_ship_date || '',
      actual_ship_date: shipment.actual_ship_date || '',
      expected_arrival: shipment.expected_arrival || '',
      actual_arrival: shipment.actual_arrival || '',
      receipt_date: shipment.receipt_date || '',
      status: shipment.status,
      notes: shipment.notes || '',
      is_direct_ship: shipment.is_direct_ship || false,
      ship_to_location_id: shipment.ship_to_location_id || '',
      ship_to_freeform: shipment.ship_to_freeform || ''
    })
    setEditingHeader(true)
  }

  async function saveHeader() {
    const PRE_ARRIVAL = ['created', 'scheduled', 'in_transit']
    const derivedStatus = headerForm.actual_arrival && PRE_ARRIVAL.includes(headerForm.status)
      ? 'arrived'
      : headerForm.status
    await supabase.from('shipments').update({
      po_id: headerForm.po_id || null,
      supplier_name: headerForm.supplier_name || null,
      carrier: headerForm.carrier || null,
      tracking_number: headerForm.tracking_number || null,
      expected_ship_date: headerForm.expected_ship_date || null,
      actual_ship_date: headerForm.actual_ship_date || null,
      expected_arrival: headerForm.expected_arrival || null,
      actual_arrival: headerForm.actual_arrival || null,
      receipt_date: headerForm.receipt_date || null,
      status: derivedStatus,
      notes: headerForm.notes || null,
      is_direct_ship: headerForm.is_direct_ship,
      ship_to_location_id: !headerForm.is_direct_ship && headerForm.ship_to_location_id ? headerForm.ship_to_location_id : null,
      ship_to_freeform: headerForm.is_direct_ship ? headerForm.ship_to_freeform : null
    }).eq('id', id)
    setEditingHeader(false)
    load()
  }

  async function updateStatus(status) {
    // Shipped: record the ship date (today unless already set) so the Planner can hand FBA shipments to Amazon's inbound
    const patch = status === 'in_transit' && !shipment.actual_ship_date ? { status, actual_ship_date: format(new Date(), 'yyyy-MM-dd') } : { status }
    await supabase.from('shipments').update(patch).eq('id', id)
    load()
  }

  async function addLine() {
    setSaving(true)
    await supabase.from('shipment_line_items').insert({
      shipment_id: id,
      product_id: lineForm.product_id || null,
      quantity_ordered: lineForm.quantity_ordered ? parseInt(lineForm.quantity_ordered) : null,
      quantity_shipped: null,
      quantity_received: null,
      lot_number: lineForm.lot_number || null,
      expiration_date: lineForm.expiration_date || null,
      notes: lineForm.notes || null
    })
    setSaving(false)
    setLineForm({ product_id: '', quantity_ordered: '', lot_number: '', expiration_date: '', notes: '' })
    setShowAllProducts(false)
    setLineModal(false)
    load()
  }

  async function updateLineQty(lineId, field, value) {
    await supabase.from('shipment_line_items').update({ [field]: value !== '' && value != null ? parseInt(value) : null }).eq('id', lineId)
    load()
  }

  async function deleteLine(lid) {
    if (window.confirm('Remove this item?')) {
      await supabase.from('shipment_line_items').delete().eq('id', lid)
      load()
    }
  }

  const totalOrdered = lines.reduce((s, l) => s + Number(l.quantity_ordered || 0), 0)
  const totalShipped = lines.reduce((s, l) => s + Number(l.quantity_shipped || 0), 0)
  const totalReceived = lines.reduce((s, l) => s + Number(l.quantity_received || 0), 0)

  function getPOCoverage() {
    if (!poLines.length) return null
    return poLines.map(pol => {
      const inShipment = lines.filter(l => l.product_id === pol.product_id).reduce((sum, l) => sum + Number(l.quantity_ordered || 0), 0)
      return { ...pol, inShipment }
    })
  }

  const coverage = getPOCoverage()
  const poTotalOrdered = coverage ? coverage.reduce((sum, c) => sum + Number(c.quantity_ordered), 0) : 0

  function getDestination() {
    if (!shipment) return '—'
    if (shipment.is_direct_ship) return <span style={{ fontStyle: 'italic' }}>{shipment.ship_to_freeform || 'Direct Ship'}</span>
    if (shipment.ship_to_loc) return shipment.ship_to_loc.name
    return '—'
  }

  if (!shipment) return <div className="loading-screen"><div className="spinner" /></div>

  const hf = k => v => setHeaderForm(f => ({ ...f, [k]: v }))

  function expiryBadge(expDate) {
    if (!expDate) return null
    const days = Math.round((new Date(expDate + 'T12:00:00') - new Date()) / (1000 * 60 * 60 * 24))
    if (days < 0) return <span className="badge badge-danger" style={{ fontSize: '10px' }}>Expired</span>
    if (days < 90) return <span className="badge badge-danger" style={{ fontSize: '10px' }}>{days}d left</span>
    if (days < 180) return <span className="badge badge-warning" style={{ fontSize: '10px' }}>{days}d left</span>
    return null
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/shipments')}><ArrowLeft size={14} />Back</button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <h1 style={{ fontFamily: 'var(--mono)' }}>{shipment.shipment_number || 'Shipment'}<HelpLink section="shipments" /></h1>
              <select className="select" style={{ width: 'auto', fontSize: '12px', padding: '4px 8px' }} value={shipmentSelectValue(shipment.status)} onChange={e => updateStatus(e.target.value)}>
                {SHIPMENT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {landed && <span className="badge badge-success" title="Arrival date is before the latest inventory upload for this destination">Landed</span>}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>
              {shipment.carrier}{shipment.tracking_number && ` · ${shipment.tracking_number}`}
              {shipment.purchase_orders && <> · PO: <Link to={`/purchase-orders/${shipment.po_id}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>{shipment.purchase_orders.po_number}</Link></>}
              {!shipment.po_id && <span style={{ color: 'var(--warning)', marginLeft: '4px' }}>· No PO linked</span>}
            </div>
          </div>
        </div>
        {!editingHeader && (
          <button className="btn btn-secondary btn-sm" onClick={startEditHeader}><Edit2 size={13} />Edit Details</button>
        )}
      </div>

      {editingHeader ? (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Edit Shipment Details</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingHeader(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={saveHeader}><Check size={13} />Save</button>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Linked PO</label>
              <select className="select" value={headerForm.po_id} onChange={e => hf('po_id')(e.target.value)}>
                <option value="">— None —</option>
                {pos.map(p => <option key={p.id} value={p.id}>{p.po_number}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Ship To</label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: !headerForm.is_direct_ship ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${!headerForm.is_direct_ship ? 'var(--accent)' : 'var(--border)'}` }}>
                  <input type="radio" checked={!headerForm.is_direct_ship} onChange={() => setHeaderForm(f => ({ ...f, is_direct_ship: false }))} />
                  Location
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer', flex: 1, padding: '8px 12px', background: headerForm.is_direct_ship ? 'var(--accent-bg)' : 'var(--bg-3)', borderRadius: 'var(--radius)', border: `1px solid ${headerForm.is_direct_ship ? 'var(--accent)' : 'var(--border)'}` }}>
                  <input type="radio" checked={headerForm.is_direct_ship} onChange={() => setHeaderForm(f => ({ ...f, is_direct_ship: true }))} />
                  Direct Ship / Sample
                </label>
              </div>
              {!headerForm.is_direct_ship ? (
                <select className="select" value={headerForm.ship_to_location_id} onChange={e => hf('ship_to_location_id')(e.target.value)}>
                  <option value="">— Select location —</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              ) : (
                <input className="input" placeholder="e.g. Sarah Chen - Sample" value={headerForm.ship_to_freeform} onChange={e => hf('ship_to_freeform')(e.target.value)} />
              )}
            </div>
            <div className="form-group"><label className="form-label">Supplier</label><input className="input" placeholder="e.g. Acme Manufacturing" value={headerForm.supplier_name} onChange={e => hf('supplier_name')(e.target.value)} /></div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Carrier</label><input className="input" value={headerForm.carrier} onChange={e => hf('carrier')(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Tracking Number</label><input className="input" value={headerForm.tracking_number} onChange={e => hf('tracking_number')(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Expected Ship Date</label><input className="input" type="date" value={headerForm.expected_ship_date} onChange={e => hf('expected_ship_date')(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Actual Ship Date</label><input className="input" type="date" value={headerForm.actual_ship_date} onChange={e => hf('actual_ship_date')(e.target.value)} /></div>
            </div>
            <div className="form-grid">
              <div className="form-group"><label className="form-label">Expected Arrival</label><input className="input" type="date" value={headerForm.expected_arrival} onChange={e => hf('expected_arrival')(e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Actual Arrival</label><input className="input" type="date" value={headerForm.actual_arrival} onChange={e => hf('actual_arrival')(e.target.value)} /></div>
            </div>
            <div className="form-group" style={{ maxWidth: '50%' }}>
              <label className="form-label">Receipt Date</label>
              <input className="input" type="date" value={headerForm.receipt_date} onChange={e => hf('receipt_date')(e.target.value)} />
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={headerForm.notes} onChange={e => hf('notes')(e.target.value)} /></div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
          {[
            { label: 'Supplier', value: shipment.supplier_name || '—' },
            { label: 'Destination', value: getDestination() },
            { label: 'Carrier', value: shipment.carrier || '—' },
            { label: 'Expected Ship', value: shipment.expected_ship_date ? format(new Date(shipment.expected_ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
            { label: 'Actual Ship', value: shipment.actual_ship_date ? format(new Date(shipment.actual_ship_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
            { label: 'Expected Arrival', value: shipment.expected_arrival ? format(new Date(shipment.expected_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—' },
            { label: 'Actual Arrival', value: shipment.actual_arrival ? format(new Date(shipment.actual_arrival + 'T12:00:00'), 'MMM d, yyyy') : '—' },
            { label: 'Receipt Date', value: shipment.receipt_date ? format(new Date(shipment.receipt_date + 'T12:00:00'), 'MMM d, yyyy') : '—' },
          ].map(({ label, value }) => (
            <div className="card" key={label} style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>{label}</div>
              <div style={{ fontSize: '13px', fontWeight: 500 }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {coverage && coverage.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 600 }}>PO Coverage</h3>
            <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>
              <strong style={{ color: totalOrdered >= poTotalOrdered ? 'var(--success)' : 'var(--warning)' }}>
                {totalOrdered} of {poTotalOrdered} units ordered
              </strong>
              {poTotalOrdered > 0 && <span> ({Math.round(totalOrdered / poTotalOrdered * 100)}%)</span>}
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>SKU</th><th>Product</th><th>PO Qty</th><th>In This Shipment</th></tr></thead>
              <tbody>
                {coverage.map(c => (
                  <tr key={c.id}>
                    <td><span className="mono">{c.products?.sku || '—'}</span></td>
                    <td>{c.products?.name || c.description || '—'}</td>
                    <td>{c.quantity_ordered}</td>
                    <td style={{ color: c.inShipment > 0 ? 'var(--success)' : 'var(--text-3)' }}>{c.inShipment || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Items */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: 600 }}>Items</h3>
            {lines.length > 0 && (
              <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>
                {totalOrdered > 0 && <span>{totalOrdered} ordered</span>}
                {totalShipped > 0 && <span> · {totalShipped} shipped</span>}
                {totalReceived > 0 && <span style={{ color: 'var(--success)' }}> · {totalReceived} received</span>}
              </div>
            )}
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { setLineModal(true); setShowAllProducts(false) }}><Plus size={13} />Add Item</button>
        </div>

        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px' }}><p>No items yet</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>Ordered</th>
                  <th style={{ textAlign: 'right' }}>Shipped</th>
                  <th style={{ textAlign: 'right' }}>Received</th>
                  <th>Lot Number</th>
                  <th>Expiration</th>
                  <th>Notes</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {lines.map(l => (
                  <tr key={l.id}>
                    <td><span className="mono">{l.products?.sku || '—'}</span></td>
                    <td>{l.products?.name || '—'}</td>
                    <td style={{ textAlign: 'right' }}><QtyCell line={l} field="quantity_ordered" onSave={updateLineQty} /></td>
                    <td style={{ textAlign: 'right' }}><QtyCell line={l} field="quantity_shipped" onSave={updateLineQty} /></td>
                    <td style={{ textAlign: 'right' }}><QtyCell line={l} field="quantity_received" onSave={updateLineQty} /></td>
                    <td><span className="mono" style={{ fontSize: '12px' }}>{l.lot_number || '—'}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {l.expiration_date ? format(new Date(l.expiration_date + 'T12:00:00'), 'MMM d, yyyy') : '—'}
                        {expiryBadge(l.expiration_date)}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-3)', fontSize: '12px' }}>{l.notes || '—'}</td>
                    <td><button className="btn btn-ghost btn-sm" onClick={() => deleteLine(l.id)}><Trash2 size={13} style={{ color: 'var(--danger)' }} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {shipment.notes && !editingHeader && (
        <div className="card" style={{ padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>Notes</div>
          <p style={{ fontSize: '13px', color: 'var(--text-2)' }}>{shipment.notes}</p>
        </div>
      )}

      {/* Add Item Modal */}
      {lineModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setLineModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>Add Item</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setLineModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Product</label>
                  {poProducts.length > 0 && (
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: '11px', padding: '2px 8px' }} onClick={() => setShowAllProducts(v => !v)}>
                      {showAllProducts ? '← Show PO items only' : 'Show all products'}
                    </button>
                  )}
                </div>
                <ProductPicker
                  products={dropdownProducts}
                  value={lineForm.product_id}
                  onChange={id => setLineForm(f => ({ ...f, product_id: id }))}
                  placeholder="Search for a product..."
                />
                {poProducts.length > 0 && !showAllProducts && (
                  <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '4px' }}>Showing {poProducts.length} items from linked PO</div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Qty Ordered *</label>
                <input className="input" type="number" value={lineForm.quantity_ordered} onChange={e => setLineForm(f => ({ ...f, quantity_ordered: e.target.value }))} placeholder="0" />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Lot Number <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="input" value={lineForm.lot_number} onChange={e => setLineForm(f => ({ ...f, lot_number: e.target.value }))} placeholder="LOT-12345" />
                </div>
                <div className="form-group">
                  <label className="form-label">Expiration Date <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>(optional)</span></label>
                  <input className="input" type="date" value={lineForm.expiration_date} onChange={e => setLineForm(f => ({ ...f, expiration_date: e.target.value }))} />
                </div>
              </div>
              <div className="form-group"><label className="form-label">Notes</label><textarea className="textarea" rows={2} value={lineForm.notes} onChange={e => setLineForm(f => ({ ...f, notes: e.target.value }))} /></div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setLineModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addLine} disabled={saving || !lineForm.quantity_ordered}>{saving ? 'Saving...' : 'Add Item'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
